// Per-chat canvas surface.
//
// The chat agent can pin renders to a persistent canvas alongside the
// chat transcript via `canvas_add` / `canvas_update` / `canvas_remove`
// / `canvas_clear`. The state lives on the chat object (chat.canvas)
// so it survives navigation between chats but resets with the chat.
//
// Each item is a (render_spec, data) pair — the same shape `render_block`
// uses — plus an `id` (so the agent can address it for updates) and an
// optional `caption` (curated commentary the user reads on the surface).
// Items are NOT live-recomputed; the data snapshot is frozen at pin time.
// To refresh, the agent calls `canvas_update(id, …)` with fresh data.

const ID_PREFIX = "canv";
let counter = 0;
function nextId() {
    counter += 1;
    // Combining time + counter keeps ids both unique within a tick
    // (counter) and somewhat sortable across ticks (time prefix).
    return `${ID_PREFIX}_${Date.now().toString(36)}_${counter.toString(36)}`;
}

export function createEmptyCanvas() {
    return { items: [] };
}

export function normalizeCanvas(canvas) {
    if (!canvas || typeof canvas !== "object") return createEmptyCanvas();
    return {
        items: Array.isArray(canvas.items) ? canvas.items : [],
    };
}

// Returns { items, addedId }. Caller persists items; addedId is the
// new item's identifier so the tool can echo it back to the agent.
export function addItem(canvas, { spec, data, caption, sourceToolCallIds }) {
    const id = nextId();
    const item = {
        id,
        spec,
        data,
        caption: typeof caption === "string" && caption.trim() ? caption.trim() : null,
        // Provenance hint — list of `_tool_call_id`s the agent declared
        // produced this item's `data`. Optional but strongly preferred:
        // the canvas-to-kernels save flow uses these to identify the
        // right transcript chain instead of doing a full heuristic
        // scan. Falsy means "agent didn't tag, save will fall back".
        sourceToolCallIds: sanitizeToolCallIds(sourceToolCallIds),
        // Saved-kernel handle. Filled in when the canvas-to-kernels
        // flow crystallises this item into a persisted kernel. Shape:
        // `{ command, savedAt }`. Lets the canvas show "saved as
        // /command" across page refreshes and lets a re-save reuse
        // the same command (overwriting) instead of suffixing -2.
        savedKernel: null,
        addedAt: Date.now(),
    };
    return {
        items: [...normalizeCanvas(canvas).items, item],
        addedId: id,
    };
}

function sanitizeToolCallIds(value) {
    if (!Array.isArray(value)) return null;
    const cleaned = value
        .filter((v) => typeof v === "string" && v.trim())
        .map((v) => v.trim());
    return cleaned.length ? cleaned : null;
}

// Returns { items, found }. Partial — only fields present in `partial`
// replace the existing values. Pass {spec: undefined} explicitly to
// avoid clobbering it; we only treat keys that ARE present in partial
// as updates (Object.prototype.hasOwnProperty.call).
export function updateItem(canvas, id, partial) {
    const items = normalizeCanvas(canvas).items;
    const idx = items.findIndex((it) => it.id === id);
    if (idx === -1) return { items, found: false };
    const next = { ...items[idx] };
    if (Object.prototype.hasOwnProperty.call(partial, "spec")) next.spec = partial.spec;
    if (Object.prototype.hasOwnProperty.call(partial, "data")) next.data = partial.data;
    if (Object.prototype.hasOwnProperty.call(partial, "caption")) {
        next.caption = typeof partial.caption === "string" && partial.caption.trim()
            ? partial.caption.trim()
            : null;
    }
    if (Object.prototype.hasOwnProperty.call(partial, "sourceToolCallIds")) {
        next.sourceToolCallIds = sanitizeToolCallIds(partial.sourceToolCallIds);
    }
    if (Object.prototype.hasOwnProperty.call(partial, "savedKernel")) {
        const sk = partial.savedKernel;
        next.savedKernel = sk && typeof sk === "object" && typeof sk.command === "string"
            ? { command: sk.command, savedAt: Number(sk.savedAt) || Date.now() }
            : null;
    }
    next.updatedAt = Date.now();
    return {
        items: items.map((it, i) => (i === idx ? next : it)),
        found: true,
    };
}

export function removeItem(canvas, id) {
    const items = normalizeCanvas(canvas).items;
    const filtered = items.filter((it) => it.id !== id);
    return { items: filtered, found: filtered.length !== items.length };
}

export function clearItems() {
    return { items: [] };
}

// Returns a small text summary suitable for canvas_inspect's content.
// Spec/data are intentionally NOT inlined — full inspection of a single
// item belongs in a follow-up call (or the agent can just call update).
// Provenance count is surfaced per item + a footer reminder when any
// items are missing sourceToolCallIds, so the agent has a clear nudge
// to backfill the hint via canvas_update before the user crystallises
// the canvas into saved kernels.
export function summariseCanvas(canvas) {
    const items = normalizeCanvas(canvas).items;
    if (!items.length) return "canvas: empty (no pinned items)";
    const missingProvenance = items.filter((it) => !it.sourceToolCallIds || !it.sourceToolCallIds.length);
    const savedCount = items.filter((it) => it.savedKernel?.command).length;
    const lines = [
        `canvas: ${items.length} item${items.length === 1 ? "" : "s"}${savedCount > 0 ? ` (${savedCount} crystallised to saved kernels)` : ""}`,
        ...items.map((it, i) => {
            const kind = it.spec?.kind || "?";
            const title = it.spec?.title || it.spec?.label || null;
            const cap = it.caption || title;
            const captionTail = cap ? ` — ${truncate(cap, 80)}` : "";
            const provCount = Array.isArray(it.sourceToolCallIds) ? it.sourceToolCallIds.length : 0;
            const provTail = provCount > 0
                ? ` · provenance: ${provCount} tool call${provCount === 1 ? "" : "s"}`
                : ` · provenance: NONE`;
            const savedTail = it.savedKernel?.command
                ? ` · saved as /${it.savedKernel.command}`
                : "";
            return `  ${i + 1}. id=${it.id} · kind=${kind}${captionTail}${provTail}${savedTail}`;
        }),
    ];
    if (missingProvenance.length) {
        lines.push(
            "",
            `${missingProvenance.length} item${missingProvenance.length === 1 ? "" : "s"} missing provenance.`,
            "Call canvas_update on each with `sourceToolCallIds: [<_tool_call_id>, ...]` listing the analytics_query / execute_python tool-call ids whose output produced that item's data. Without them, later crystallising the canvas into saved kernels has to scan the whole transcript heuristically.",
        );
    }
    return lines.join("\n");
}

function truncate(s, n) {
    if (!s) return "";
    const str = String(s);
    return str.length > n ? str.slice(0, n - 1) + "…" : str;
}
